import React, { useState, useEffect } from 'react';
import {fetchUsers, generateAccessCode, updateUserByAdmin, sendEmailToUsers, applyUserPenalty} from '../../api/api';
import styles from './AdminUsersForm.module.css';
import CustomModal from "../../UI/Modal/CustomModal";
import Spinner from "../Spinner/Spinner";

const PENALTY_LABELS = {
    0: 'Brak / reset',
    1: 'Ostrzeżenie (tylko mail)',
    2: 'Blokada 1 tydzień',
    3: 'Blokada 2 tygodnie',
    4: 'Blokada 1 miesiąc',
};

// "2026-07-02" -> "02-07-2026"
const formatPlDate = (isoDate) => {
    if (!isoDate) return '';
    const [y, m, d] = isoDate.split('-');
    return `${d}-${m}-${y}`;
};

const isBlockActive = (isoDate) => {
    if (!isoDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(isoDate) >= today;
};

const AdminUsersForm = () => {
    const [users, setUsers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalAction, setModalAction] = useState(null);
    const [lastName, setLastName] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [emailFilter, setEmailFilter] = useState('');
    const [firstNameFilter, setFirstNameFilter] = useState('');
    const [lastNameFilterState, setLastNameFilterState] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [memberFilter, setMemberFilter] = useState(false);

    const [emailModal, setEmailModal] = useState(false);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [infoMessage, setInfoMessage] = useState('');
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [infoPositive, setInfoPositive] = useState(true);

    const [penaltyModal, setPenaltyModal] = useState(false);
    const [penaltyUser, setPenaltyUser] = useState(null);
    const [penaltyLevel, setPenaltyLevel] = useState(0);
    const [penaltyReason, setPenaltyReason] = useState('');
    const [penaltySendEmail, setPenaltySendEmail] = useState(true);
    const [isApplyingPenalty, setIsApplyingPenalty] = useState(false);

    const getUsers = async () => {
        try {
            const fetchedUsers = await fetchUsers();
            if (Array.isArray(fetchedUsers)) {
                setUsers(fetchedUsers);
            } else {
                console.error('Fetched data is not an array:', fetchedUsers);
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getUsers();
    }, []);


    const filteredUsers = users.filter(user => {
        const emailMatch = user.username.toLowerCase().includes(emailFilter.toLowerCase());
        const firstNameMatch = user.firstName.toLowerCase().includes(firstNameFilter.toLowerCase());
        const lastNameMatch = user.lastName.toLowerCase().includes(lastNameFilterState.toLowerCase());
        const phoneMatch = user.phone?.toLowerCase().includes(phoneFilter.toLowerCase());
        const memberMatch = !memberFilter || user.member;
        return emailMatch && firstNameMatch && lastNameMatch && phoneMatch && memberMatch;
    });



    const handleReservationToggle = async (id, enabledReservation, lastName) => {
        setModalAction(() => () => updateUserByAdmin(id, { enabledReservation: !enabledReservation }));
        setShowModal(true);
        setLastName(lastName);
    };

    const handleMembershipToggle = async (id, isMember, lastName) => {
        setModalAction(() => () => updateUserByAdmin(id, { isMember: !isMember }));
        setShowModal(true);
        setLastName(lastName);
    };

    const handleFirstInstallmentToggle = async (id, firstInstallmentPaid, lastName) => {
        setModalAction(() => () => updateUserByAdmin(id, { firstInstallmentPaid: !firstInstallmentPaid }));
        setShowModal(true);
        setLastName(lastName);
    };

    const handleCoachshipToggle = async (id, isCoach, lastName) => {
        setModalAction(() => () => updateUserByAdmin(id, { isCoach: !isCoach }));
        setShowModal(true);
        setLastName(lastName);
    };

    const handleDateChange = async (id, newDate) => {
        try {
            await updateUserByAdmin(id, { payedToDate: newDate || null });
            getUsers();
        } catch (error) {
            console.error('Błąd podczas aktualizacji daty płatności użytkownika.', error);
        }
    };


    const openPenaltyModal = (user) => {
        const current = user.reservationPenaltyLevel || 0;
        const proposed = Math.min(current + 1, 4);
        setPenaltyUser(user);
        setPenaltyLevel(proposed);
        setPenaltyReason('');
        setPenaltySendEmail(true);
        setPenaltyModal(true);
    };

    const submitPenalty = async () => {
        if (!penaltyUser) return;
        setIsApplyingPenalty(true);
        try {
            await applyUserPenalty(penaltyUser.id, {
                level: penaltyLevel,
                reason: penaltyReason || null,
                sendEmail: penaltySendEmail,
            });
            setPenaltyModal(false);
            await getUsers();
            setInfoMessage(penaltyLevel === 0
                ? `Blokada dla ${penaltyUser.lastName} została zresetowana.`
                : `Nałożono blokadę (${PENALTY_LABELS[penaltyLevel]}) dla ${penaltyUser.lastName}.`);
            setInfoPositive(true);
            setShowInfoModal(true);
        } catch (error) {
            setPenaltyModal(false);
            setInfoMessage('Błąd podczas nakładania blokady.');
            setInfoPositive(false);
            setShowInfoModal(true);
        } finally {
            setIsApplyingPenalty(false);
        }
    };

    const confirmModalAction = async () => {
        setShowModal(false);
        try {
            await modalAction();
            getUsers();
        } catch (error) {
            console.error('Błąd podczas wykonywania akcji modalu.', error);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setLastName(null);
    };

    const handleOpenEmailModal = () => {
        setEmailSubject('');
        setEmailBody('');
        setEmailModal(true);
    };

    const handleSendEmail = async () => {
        if (!emailSubject.trim() || !emailBody.trim()) return;
        const recipients = filteredUsers.map(u => u.username);
        if (recipients.length === 0) return;
        setIsSendingEmail(true);
        const htmlMessage = emailBody.replace(/\n/g, '<br>');
        const result = await sendEmailToUsers(recipients, emailSubject, htmlMessage);
        setIsSendingEmail(false);
        setEmailModal(false);
        setInfoMessage(result.success
            ? `Wiadomość wysłana do ${recipients.length} użytkowników`
            : 'Błąd wysyłki: ' + (result.data?.message || result.data || 'Nieznany błąd'));
        setInfoPositive(result.success);
        setShowInfoModal(true);
    };

    if (isLoading) return <Spinner size={"large"}/>;

    return (
        <>
            <CustomModal
                isOpen={showModal}
                onRequestClose={closeModal}
                message={`Czy na pewno chcesz wykonać tę akcję na ${lastName} ?`}
                confirmLabel="Wykonaj"
                onConfirm={confirmModalAction}
                confirmButtonColor="green"
                cancelLabel="Anuluj"
                cancelButtonColor="red"
                onCancel={closeModal}
            />

            <CustomModal
                isOpen={showInfoModal}
                onRequestClose={() => setShowInfoModal(false)}
                message={infoMessage}
                isPositiveMessage={infoPositive}
                cancelLabel="Zamknij"
                cancelButtonColor="green"
            />

            <div className={styles.filterContainer}>
                <input
                    type="text"
                    placeholder="Email"
                    className={styles.filterInput}
                    value={emailFilter}
                    onChange={(e) => setEmailFilter(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="Imię"
                    className={styles.filterInput}
                    value={firstNameFilter}
                    onChange={(e) => setFirstNameFilter(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="Nazwisko"
                    className={styles.filterInput}
                    value={lastNameFilterState}
                    onChange={(e) => setLastNameFilterState(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="Telefon"
                    className={styles.filterInput}
                    value={phoneFilter}
                    onChange={(e) => setPhoneFilter(e.target.value)}
                />
                <label className={styles.memberCheckbox}>
                    <input
                        type="checkbox"
                        checked={memberFilter}
                        onChange={(e) => setMemberFilter(e.target.checked)}
                    />
                    BTT
                </label>
                <button className={styles.mailButton} onClick={handleOpenEmailModal}>
                    Mail ({filteredUsers.length})
                </button>
            </div>

            <div className={styles.superAdmin}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>Email</th>
                        <th>Telefon</th>
                        <th>Imię</th>
                        <th>Nazwisko</th>
                        <th>Rez</th>
                        <th>BTT</th>
                        <th>1 rata</th>
                        <th>Coach</th>
                        <th>Okres</th>
                        <th>Kod</th>
                        <th>Blokada</th>

                    </tr>
                    </thead>
                    <tbody>
                    {filteredUsers
                        .sort((a, b) => a.lastName.localeCompare(b.lastName))
                        .map((user) => (
                            <tr key={user.id}>
                                <td>{user.username}</td>
                                <td>{user.phone}</td>
                                <td>{user.firstName}</td>
                                <td>{user.lastName}</td>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={user.enabledReservation}
                                        onChange={() => handleReservationToggle(user.id, user.enabledReservation, user.lastName)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={user.member}
                                        onChange={() => handleMembershipToggle(user.id, user.member, user.lastName)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={user.firstInstallmentPaid}
                                        onChange={() => handleFirstInstallmentToggle(user.id, user.firstInstallmentPaid, user.lastName)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={user.coach}
                                        onChange={() => handleCoachshipToggle(user.id, user.coach, user.lastName)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="date"
                                        value={user.payedToDate ? user.payedToDate : ""}
                                        onChange={(event) => handleDateChange(user.id, event.target.value)}
                                    />
                                </td>
                                <td>
                                    <div className={styles.code}>
                                        <span>{user.doorAccessCode || "-"}</span>
                                        <button
                                            className={styles.button}
                                            onClick={async () => {
                                                try {
                                                    await generateAccessCode(user.id);
                                                    getUsers();
                                                } catch (error) {
                                                    alert("Błąd podczas generowania kodu.");
                                                }
                                            }}
                                            style={{ padding: '4px 8px' }}
                                        >
                                            gen
                                        </button>

                                    </div>
                                </td>
                                <td>
                                    <div className={styles.code}>
                                        <span>
                                            {(user.reservationPenaltyLevel || 0) > 0
                                                ? `P${user.reservationPenaltyLevel}`
                                                : '-'}
                                            {isBlockActive(user.reservationBlockedUntil)
                                                ? ` (do ${formatPlDate(user.reservationBlockedUntil)})`
                                                : ''}
                                        </span>
                                        <button
                                            className={styles.button}
                                            onClick={() => openPenaltyModal(user)}
                                            style={{ padding: '4px 8px' }}
                                        >
                                            blokada
                                        </button>
                                    </div>
                                </td>

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {emailModal && (
                <div className={styles.emailOverlay} onClick={() => setEmailModal(false)}>
                    <div className={styles.emailModalContent} onClick={e => e.stopPropagation()}>
                        <h3>Wiadomość do {filteredUsers.length} użytkowników</h3>
                        <label className={styles.emailLabel}>Temat:</label>
                        <input
                            type="text"
                            className={styles.emailInput}
                            value={emailSubject}
                            onChange={e => setEmailSubject(e.target.value)}
                            placeholder="Temat wiadomości"
                        />
                        <label className={styles.emailLabel}>Treść:</label>
                        <textarea
                            className={styles.emailTextarea}
                            value={emailBody}
                            onChange={e => setEmailBody(e.target.value)}
                            rows={8}
                            placeholder="Treść wiadomości (nowe linie zostaną zachowane)"
                        />
                        <div className={styles.emailButtons}>
                            <button className={styles.emailBtnCancel} onClick={() => setEmailModal(false)}>Anuluj</button>
                            <button
                                className={styles.emailBtnSend}
                                onClick={handleSendEmail}
                                disabled={isSendingEmail || !emailSubject.trim() || !emailBody.trim()}
                            >
                                {isSendingEmail ? 'Wysyłanie...' : 'Wyślij'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {penaltyModal && penaltyUser && (
                <div className={styles.emailOverlay} onClick={() => setPenaltyModal(false)}>
                    <div className={styles.emailModalContent} onClick={e => e.stopPropagation()}>
                        <h3>Blokada dla {penaltyUser.firstName} {penaltyUser.lastName}</h3>
                        <p style={{ margin: '4px 0' }}>
                            Aktualny poziom: <strong>{PENALTY_LABELS[penaltyUser.reservationPenaltyLevel || 0]}</strong>
                            {isBlockActive(penaltyUser.reservationBlockedUntil)
                                ? ` — blokada do ${formatPlDate(penaltyUser.reservationBlockedUntil)}`
                                : ''}
                        </p>
                        <label className={styles.emailLabel}>Poziom blokady:</label>
                        <select
                            className={styles.emailInput}
                            value={penaltyLevel}
                            onChange={e => setPenaltyLevel(Number(e.target.value))}
                        >
                            {[0, 1, 2, 3, 4].map(lvl => (
                                <option key={lvl} value={lvl}>{lvl} — {PENALTY_LABELS[lvl]}</option>
                            ))}
                        </select>
                        <label className={styles.emailLabel}>Powód (opcjonalnie):</label>
                        <textarea
                            className={styles.emailTextarea}
                            value={penaltyReason}
                            onChange={e => setPenaltyReason(e.target.value)}
                            rows={3}
                            placeholder="Powód nałożenia blokady"
                        />
                        <label className={styles.memberCheckbox} style={{ marginTop: '8px' }}>
                            <input
                                type="checkbox"
                                checked={penaltySendEmail}
                                onChange={e => setPenaltySendEmail(e.target.checked)}
                            />
                            Wyślij e-mail do gracza
                        </label>
                        <div className={styles.emailButtons}>
                            <button className={styles.emailBtnCancel} onClick={() => setPenaltyModal(false)}>Anuluj</button>
                            <button
                                className={styles.emailBtnSend}
                                onClick={submitPenalty}
                                disabled={isApplyingPenalty}
                            >
                                {isApplyingPenalty ? 'Zapisywanie...' : (penaltyLevel === 0 ? 'Resetuj' : 'Nałóż')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>

    );

};

export default AdminUsersForm;
